import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import logo from '../../assets/images/logo.png';
import { useNavigate } from 'react-router-dom';
import { useToken } from '../../../app/hooks';
import { DeskTopCalVW, media } from '../../../utils/CalVW';
import { allowHiddenFeatureForUser, isLocalEnvironment } from '../../../utils/env';
import { useUserInfo } from '../../../HomeTown/hooks/Auth/GetUserInfo';
import {HomeTownSignUpModal} from "../../../app/organisms/Auth/Forms/HomeTownSignUpModal";
import {HomeTownCustomerSignUpModal} from "../../../app/organisms/Auth/Forms/HomeTownCustomerSignUpModal";
import {HomeTownRealEstateSignUpModal} from "../../../app/organisms/Auth/Forms/HomeTownRealEstateSignUpModal";

const BREAKPOINT = 768;

/* ===== Root Vars (색/블러/섀도) ===== */
const Shell = styled.header`
    --bg: rgba(255,255,255,0.7);
    --blur: 14px;
    --line: rgba(255,255,255,0.6);
    --shadow: 0 8px 24px rgba(16, 24, 40, 0.06);
    --ink: #0f172a;
    --muted: #64748b;
    --brand: #4f46e5;

    position: sticky;
    top: 0;
    z-index: 1000;
    backdrop-filter: blur(var(--blur));
    -webkit-backdrop-filter: blur(var(--blur));
    background: var(--bg);
    box-shadow: inset 0 -1px 0 rgba(15, 23, 42, 0.06);
`;

/* ===== Container ===== */
const NavBarContainer = styled.div`
    margin: 0 auto;
    width: 100%;
    max-width: 1280px;

    display: grid;
    grid-template-columns: auto 1fr auto;
    align-items: center;
    gap: 12px;

    height: 72px;
    padding: 0 16px;

    @media (min-width: ${BREAKPOINT + 1}px) {
        height: 84px;
        padding: 0 24px;
    }

    @media ${media.desktop} {
        padding-left: ${DeskTopCalVW(24)};
        padding-right: ${DeskTopCalVW(24)};
        padding-top: ${DeskTopCalVW(24)};
        padding-bottom: ${DeskTopCalVW(24)};
    }
`;

/* ===== Logo ===== */
const LogoBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: transparent;
  border: none;
  padding: 8px 10px;
  border-radius: 14px;
  cursor: pointer;

  &:hover {
    background: rgba(15, 23, 42, 0.04);
  }

  img {
    width: 132px;
    height: auto;

    @media (min-width: ${BREAKPOINT + 1}px) {
      width: 148px;
    }

    @media ${media.desktop} {
      width: 100px;
    }
  }
`;

/* ===== Desktop Menu ===== */
const DesktopNav = styled.nav`
    display: none;
    @media (min-width: ${BREAKPOINT + 1}px) {
        display: flex;
        justify-content: center;
    }
`;

const MenuList = styled.ul`
    display: flex;
    align-items: center;
    gap: clamp(12px, 2.4vw, 28px);
`;

const MenuItem = styled.li`
    position: relative;
    color: var(--ink);
    font-size: clamp(14px, 1.1vw, 16px);
    font-weight: 600;
    letter-spacing: 0.01em;
    padding: 10px 8px;
    cursor: pointer;

    &::after {
        content: '';
        position: absolute;
        left: 8px;
        right: 8px;
        bottom: 6px;
        height: 2px;
        border-radius: 2px;
        transform: scaleX(0);
        transform-origin: left;
        transition: transform .2s ease;
        background: linear-gradient(90deg, var(--brand), #06b6d4);
    }
    &:hover::after { transform: scaleX(1); }
    &:hover { color: #111827; }
`;

/* ===== Right (CTA / Auth) ===== */
const RightWrap = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    justify-self: end;
`;

const GhostBtn = styled.button`
    height: 40px;
    padding: 0 14px;
    border-radius: 12px;
    border: 1px solid rgba(15, 23, 42, 0.08);
    background: rgba(255,255,255,0.7);
    color: var(--ink);
    font-weight: 600;
    letter-spacing: 0.01em;
    cursor: pointer;
    transition: transform .06s ease, background .2s ease, border-color .2s ease;

    &:hover {
        background: rgba(255,255,255,0.95);
        border-color: rgba(15, 23, 42, 0.14);
    }
    &:active { transform: translateY(1px); }

    @media (max-width: ${BREAKPOINT}px) {
        display: none;
    }
`;

const PrimaryBtn = styled.button`
    height: 40px;
    padding: 0 16px;
    border-radius: 12px;
    border: 0;
    background: linear-gradient(135deg, var(--brand), #06b6d4 120%);
    box-shadow: var(--shadow);
    color: #fff;
    font-weight: 700;
    letter-spacing: 0.01em;
    cursor: pointer;
    transition: transform .06s ease, filter .2s ease;

    &:hover { filter: brightness(1.03); }
    &:active { transform: translateY(1px); }

    @media (max-width: ${BREAKPOINT}px) {
        display: none;
    }
`;

/* ===== Mobile Controls ===== */
const BurgerButton = styled.button`
    width: 44px;
    height: 44px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: 1px solid rgba(15, 23, 42, 0.08);
    border-radius: 12px;
    cursor: pointer;

    @media (min-width: ${BREAKPOINT + 1}px) {
        display: none;
    }

    span, span::before, span::after {
        content: '';
        display: block;
        width: 20px;
        height: 2px;
        background: #111827;
        border-radius: 2px;
        transition: transform .2s ease, opacity .2s ease;
    }
    span { position: relative; }
    span::before { position: absolute; top: -6px; left: 0; }
    span::after  { position: absolute; top: 6px;  left: 0; }

    &.open {
        border-color: rgba(15, 23, 42, 0.18);
        background: rgba(255,255,255,0.85);
    }
    &.open span { background: transparent; }
    &.open span::before { transform: translateY(6px) rotate(45deg); }
    &.open span::after  { transform: translateY(-6px) rotate(-45deg); }
`;

/* ===== Drawer (Mobile) ===== */
const DrawerOverlay = styled.div.attrs(({ $open }) => ({
    'aria-hidden': $open ? 'false' : 'true',
}))`
    position: fixed;
    inset: 0;
    background: rgba(15, 23, 42, .32);
    backdrop-filter: blur(3px);
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
    transition: opacity .2s ease, visibility .2s ease;
    z-index: 999;

    ${({ $open }) => $open && `
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
  `}
`;

const DrawerPanel = styled.nav.attrs(({ $open }) => ({
    'data-open': $open ? 'true' : 'false',
    role: 'dialog',
    'aria-modal': 'true',
}))`
    position: fixed;
    top: 0;
    right: 0;
    height: 100dvh;
    width: min(86vw, 360px);
    background: rgba(255,255,255,0.9);
    backdrop-filter: blur(16px);
    -webkit-backdrop-filter: blur(16px);
    border-radius: 18px 0 0 18px;
    border: 1px solid rgba(15,23,42,0.08);
    box-shadow: 0 20px 50px rgba(15,23,42,0.18);
    z-index: 1000;

    /* ✅ 완전 오프캔버스 */
    transform: translateX(100%);
    opacity: 0;
    visibility: hidden;
    pointer-events: none;

    will-change: transform, opacity;

    transition:
            transform .26s cubic-bezier(.22,.61,.36,1),
            opacity .2s ease,
            visibility .2s ease;

    @media (min-width: 769px) { display: none; }

    &[data-open="true"] {
        transform: translateX(0%);
        opacity: 1;
        visibility: visible;
        pointer-events: auto;
    }
`;

const DrawerHead = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px;
    border-bottom: 1px solid rgba(15,23,42,0.06);
`;

const DrawerTitle = styled.div`
    font-size: 16px;
    font-weight: 800;
    color: #0f172a;
    letter-spacing: 0.02em;
`;

const DrawerClose = styled.button`
    width: 38px;
    height: 38px;
    border: 1px solid rgba(15,23,42,0.08);
    border-radius: 12px;
    background: rgba(255,255,255,0.8);
    cursor: pointer;
    position: relative;

    &:before, &:after {
        content: '';
        position: absolute;
        left: 10px;
        right: 10px;
        top: 18px;
        height: 2px;
        background: #0f172a;
    }
    &:before { transform: rotate(45deg); }
    &:after  { transform: rotate(-45deg); }
`;

const DrawerBody = styled.div`
    padding: 16px 12px 20px 12px;
    overflow: auto;
    height: calc(100% - 64px - 70px); /* 헤더/풋터 높이 보정 */
`;

const DrawerMenu = styled.ul`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const DrawerItem = styled.li`
    button {
        width: 100%;
        text-align: left;
        padding: 14px 14px;
        border-radius: 14px;
        border: 1px solid rgba(15,23,42,0.06);
        background: linear-gradient(180deg, rgba(255,255,255,0.95), rgba(255,255,255,0.85));
        color: #0f172a;
        font-weight: 700;
        letter-spacing: 0.01em;
        cursor: pointer;
        transition: transform .06s ease, border-color .2s ease, background .2s ease;

        &:hover {
            border-color: rgba(15,23,42,0.14);
            background: rgba(255,255,255,0.98);
        }
        &:active { transform: translateY(1px); }
    }
`;

const DrawerFoot = styled.div`
    padding: 12px;
    border-top: 1px solid rgba(15,23,42,0.06);
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;

    button {
        height: 44px;
        border-radius: 12px;
        font-weight: 700;
        letter-spacing: .01em;
        cursor: pointer;
    }
`;

const Outline = styled.button`
    border: 1px solid rgba(15,23,42,0.12);
    background: rgba(255,255,255,0.9);
    color: #0f172a;
`;

const Solid = styled.button`
    border: 0;
    background: linear-gradient(135deg, #4f46e5, #06b6d4 130%);
    color: #fff;
`;

/* ===== Component ===== */
function NavBar() {
    const navigate = useNavigate();
    const { data: token, mutate } = useToken();
    const [isSignUpModalOpen, setIsSignUpModalOpen] = React.useState(false);
    const [isCustomerSignUpModalOpen, setIsCustomerSignUpModalOpen] = React.useState(false);
    const [isRealEstateSignUpModalOpen, setIsRealEstateSignUpModalOpen] = React.useState(false);
    const [open, setOpen] = useState(false);
    const closeDrawer = useCallback(() => setOpen(false), []);
    const toggleDrawer = useCallback(() => setOpen(v => !v), []);
    const { userInfo } = useUserInfo();

    // ESC 닫기
    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') closeDrawer(); };
        if (open) window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, closeDrawer]);

    // 배경 스크롤 잠금
    useEffect(() => {
        if (!open) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = prev; };
    }, [open]);

    const go = (path) => { navigate(path); closeDrawer(); };
    const isLocal = isLocalEnvironment();
    const canShowHidden = allowHiddenFeatureForUser(userInfo);

    return (
        <div>
            <Shell>
                <NavBarContainer>
                    <LogoBtn onClick={() => go('/')}>
                        <img src={logo} alt="logo" />
                    </LogoBtn>

                    {/* Desktop Nav */}
                    <DesktopNav>
                        <MenuList>
                            <MenuItem onClick={() => go('/')}>메인</MenuItem>
                            <MenuItem onClick={() => go('/method')}>광고방식</MenuItem>
                            <MenuItem onClick={() => go('/notice')}>공지사항</MenuItem>
                            <MenuItem onClick={() => go('/QnA')}>Q&amp;A</MenuItem>
                            {canShowHidden && <MenuItem onClick={() => go('/home-introduce')}>디지털광고물 제작업체 소개</MenuItem>}
                        </MenuList>
                    </DesktopNav>

                    {/* Right */}
                    <RightWrap>
                        {!token ? (
                            <>
                                <GhostBtn onClick={() => go('/admin/login')}>로그인</GhostBtn>
                                <PrimaryBtn onClick={() => setIsSignUpModalOpen(true)}>회원가입</PrimaryBtn>
                            </>
                        ) : (
                            <>
                                <GhostBtn onClick={() => go('/admin/login')}>로그인</GhostBtn>
                                <GhostBtn onClick={() => { mutate(null); }}>로그아웃</GhostBtn>
                            </>
                        )}

                        {/* Mobile Burger */}
                        <BurgerButton
                            aria-label={open ? '메뉴 닫기' : '메뉴 열기'}
                            aria-expanded={open}
                            aria-controls="mobile-drawer"
                            onClick={toggleDrawer}
                            className={open ? 'open' : ''}
                        >
                            <span />
                        </BurgerButton>
                    </RightWrap>
                </NavBarContainer>

                {/* Mobile Drawer - 열릴 때만 마운트 */}
                {open && (
                    <>
                        <DrawerOverlay $open onClick={closeDrawer} />
                        <DrawerPanel id="mobile-drawer" $open>
                            <DrawerHead>
                                <DrawerTitle>메뉴</DrawerTitle>
                                <DrawerClose aria-label="닫기" onClick={closeDrawer} />
                            </DrawerHead>

                            <DrawerBody>
                                <DrawerMenu>
                                    <DrawerItem><button onClick={() => go('/')}>메인</button></DrawerItem>
                                    <DrawerItem><button onClick={() => go('/method')}>광고방식</button></DrawerItem>
                                    <DrawerItem><button onClick={() => go('/notice')}>공지사항</button></DrawerItem>
                                    <DrawerItem><button onClick={() => go('/QnA')}>Q&amp;A</button></DrawerItem>
                                    {canShowHidden && <DrawerItem><button onClick={() => go('/home-introduce')}>디지털광고물 제작업체 소개</button></DrawerItem>}
                                </DrawerMenu>
                            </DrawerBody>

                            <DrawerFoot>
                                {!token ? (
                                    <>
                                        <Outline onClick={() => go('/admin/login')}>로그인</Outline>
                                        <Solid onClick={() => setIsSignUpModalOpen(true)}>회원가입</Solid>
                                    </>
                                ) : (
                                    <>
                                        <Outline onClick={() => go('/')}>마이페이지</Outline>
                                        <Solid onClick={() => { mutate(null); closeDrawer(); }}>로그아웃</Solid>
                                    </>
                                )}
                            </DrawerFoot>
                        </DrawerPanel>
                    </>
                )}
            </Shell>
            {isSignUpModalOpen &&
                <HomeTownSignUpModal setIsSignUpModalOpen={setIsSignUpModalOpen} setIsCustomerSignUpModalOpen={setIsCustomerSignUpModalOpen} setIsRealEstateSignUpModalOpen={setIsRealEstateSignUpModalOpen}/>
            }

            {isCustomerSignUpModalOpen &&
                <HomeTownCustomerSignUpModal setIsCustomerSignUpModalOpen={setIsCustomerSignUpModalOpen}/>
            }

            {isRealEstateSignUpModalOpen &&
                <HomeTownRealEstateSignUpModal setIsRealEstateSignUpModalOpen={setIsRealEstateSignUpModalOpen}/>
            }

        </div>
    );
}

export default NavBar;
